import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchReferralMailSettings,
  fetchReferralRequests,
  fetchReferralSendPreview,
  markReferralSent,
  sendReferralEmail,
} from '../../services/adminReferralService';
import { listReferralPanelContacts } from '../../services/adminReferralPanelService';
import {
  formatReferralSentSentence,
  getReferralSends,
  sortSendsNewestFirst,
} from '../../utils/referralSentDisplay';
import './ReferralRequested.css';

const formatSubmittedAt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return String(iso);
  }
};

const normCompany = (s) => (s || '').trim().toLowerCase();

const companyRowKey = (entryId, groupIndex) => `${entryId}-${groupIndex}`;

function contactsForCompany(groups, company) {
  const target = normCompany(company);
  const group = (groups || []).find((g) => normCompany(g.company) === target);
  return (group?.contacts || []).filter((c) => (c.status || 'ACTIVE') === 'ACTIVE');
}

function formatAddress(entry) {
  const parts = [entry.streetAddress, entry.city, entry.state, entry.zip].filter(
    (p) => typeof p === 'string' && p.trim()
  );
  return parts.length ? parts.join(', ') : '—';
}

function candidateDisplayName(entry) {
  const n = [entry.firstName, entry.lastName].filter(Boolean).join(' ');
  return n || entry.username || '—';
}

export default function ReferralRequested() {
  const [items, setItems] = useState([]);
  const [panelGroups, setPanelGroups] = useState([]);
  const [sourceEmail, setSourceEmail] = useState('');
  const [serverSendEnabled, setServerSendEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [panelPick, setPanelPick] = useState({});
  const [sendModal, setSendModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [requests, groups, mail] = await Promise.all([
        fetchReferralRequests(),
        listReferralPanelContacts(),
        fetchReferralMailSettings(),
      ]);
      setItems(Array.isArray(requests) ? requests : []);
      setPanelGroups(Array.isArray(groups) ? groups : []);
      setSourceEmail(mail?.sourceEmail || '');
      setServerSendEnabled(Boolean(mail?.serverSendEnabled));
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load referral requests.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openSendModal = async (entry, companyGroup, panelContact) => {
    setSendModal({
      loading: true,
      error: null,
      entry,
      companyGroup,
      panelContact,
      preview: null,
    });
    try {
      const preview = await fetchReferralSendPreview({
        requestId: entry.id,
        company: companyGroup.company || '',
        panelContactId: panelContact.id,
      });
      setSendModal((m) => (m ? { ...m, loading: false, preview } : null));
    } catch (e) {
      setSendModal((m) =>
        m
          ? {
              ...m,
              loading: false,
              error: e.response?.data?.error || e.message || 'Failed to prepare email.',
            }
          : null
      );
    }
  };

  const closeSendModal = () => setSendModal(null);

  const onConfirmSend = async () => {
    if (!sendModal?.entry || !sendModal?.companyGroup || !sendModal?.panelContact) return;
    const payload = {
      requestId: sendModal.entry.id,
      company: sendModal.companyGroup.company || '',
      panelContactId: sendModal.panelContact.id,
    };
    setSendModal((m) => (m ? { ...m, sending: true, sendError: null } : null));
    try {
      if (serverSendEnabled) {
        await sendReferralEmail(payload);
      } else {
        const mailto = sendModal?.preview?.mailto;
        if (!mailto) return;
        window.location.href = mailto;
        await markReferralSent(payload);
      }
      closeSendModal();
      await load();
    } catch (e) {
      setSendModal((m) =>
        m
          ? {
              ...m,
              sending: false,
              sendError: e.response?.data?.error || e.message || 'Failed to send email.',
            }
          : null
      );
    }
  };

  const panelOptionsByCompany = useMemo(() => {
    const map = new Map();
    for (const entry of items) {
      for (const g of entry.interestedJobs || []) {
        const key = normCompany(g.company);
        if (!map.has(key)) {
          map.set(key, contactsForCompany(panelGroups, g.company));
        }
      }
    }
    return map;
  }, [items, panelGroups]);

  return (
    <div className="ref-req">
      <header className="ref-req__header">
        <h1 className="ref-req__title">Referral requested</h1>
        <p className="ref-req__lead">
          Requests submitted by standard users through Employee referral appear here with companies and job links.
          Choose a referral panel contact per company, then send the referral email.
        </p>
        {sourceEmail ? (
          <p className="ref-req__source-email">
            Referral source email: <strong>{sourceEmail}</strong>
          </p>
        ) : null}
      </header>

      {error ? <div className="ref-req__error">{error}</div> : null}

      {loading ? (
        <p className="ref-req__muted">Loading requests…</p>
      ) : items.length === 0 ? (
        <div className="ref-req__empty">
          <p>No referral requests yet. When users submit the Employee referral form, each submission appears here.</p>
        </div>
      ) : (
        <ul className="ref-req__list">
          {items.map((entry) => (
            <li key={entry.id} className="ref-req__card">
              <div className="ref-req__card-head">
                <time className="ref-req__time" dateTime={entry.submittedAt}>
                  {formatSubmittedAt(entry.submittedAt)}
                </time>
              </div>
              <div className="ref-req__who">
                <span className="ref-req__who-name">{candidateDisplayName(entry)}</span>
                <span className="ref-req__who-meta">
                  @{entry.username}
                  {entry.email ? <> · {entry.email}</> : null}
                  {entry.phone ? <> · {entry.phone}</> : null}
                </span>
              </div>
              <div className="ref-req__jobs">
                {(entry.interestedJobs || []).length === 0 ? (
                  <p className="ref-req__muted">No companies captured for this submission.</p>
                ) : (
                  (entry.interestedJobs || []).map((g, gi) => {
                    const rowKey = companyRowKey(entry.id, gi);
                    const contacts = panelOptionsByCompany.get(normCompany(g.company)) || [];
                    const selectedId = panelPick[rowKey] || '';
                    const selectedContact = contacts.find((c) => String(c.id) === String(selectedId));
                    const sendsNewestFirst = sortSendsNewestFirst(getReferralSends(g));
                    const hasAnySend = sendsNewestFirst.length > 0;

                    return (
                      <div key={rowKey} className={`ref-req__company${hasAnySend ? ' ref-req__company--sent' : ''}`}>
                        <div className="ref-req__company-head">
                          <div className="ref-req__company-name">{g.company || '—'}</div>
                        </div>
                        <div className="ref-req__company-body">
                        {Array.isArray(g.jobUrls) && g.jobUrls.length > 0 ? (
                          <ul className="ref-req__urls">
                            {g.jobUrls.map((url, ui) =>
                              typeof url === 'string' && url.trim() ? (
                                <li key={`${gi}-${ui}-${url.slice(0, 40)}`}>
                                  <a href={url} target="_blank" rel="noopener noreferrer">
                                    {url}
                                  </a>
                                </li>
                              ) : null
                            )}
                          </ul>
                        ) : (
                          <p className="ref-req__no-urls">No job URLs</p>
                        )}
                        </div>
                        <footer className="ref-req__company-footer">
                          <div className="ref-req__panel-row">
                          <label className="ref-req__panel-label" htmlFor={`panel-${rowKey}`}>
                            Panel referrer
                          </label>
                          <select
                            id={`panel-${rowKey}`}
                            className="ref-req__panel-select"
                            value={selectedId}
                            onChange={(e) =>
                              setPanelPick((prev) => ({ ...prev, [rowKey]: e.target.value }))
                            }
                            disabled={contacts.length === 0}
                          >
                            <option value="">
                              {contacts.length === 0
                                ? 'No panel contact for this company'
                                : 'Choose referrer…'}
                            </option>
                            {contacts.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.displayName || [c.firstName, c.lastName].filter(Boolean).join(' ')}
                                {c.email ? ` · ${c.email}` : ''}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className="ref-req__send-btn"
                            onClick={() => selectedContact && openSendModal(entry, g, selectedContact)}
                            disabled={!selectedContact}
                          >
                            Send referral
                          </button>
                          </div>
                          {hasAnySend ? (
                            <ul className="ref-req__panel-sent-list" aria-label="Sent referrals">
                              {sendsNewestFirst.map((s, si) => (
                                <li key={`${rowKey}-sent-${si}-${s.panelContactId}`} className="ref-req__panel-sent-item">
                                  {formatReferralSentSentence(s)}
                                </li>
                              ))}
                            </ul>
                          ) : null}
                        </footer>
                      </div>
                    );
                  })
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {sendModal ? (
        <div className="ref-req__backdrop" role="presentation" onClick={closeSendModal}>
          <div
            className="ref-req__modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ref-req-send-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ref-req__modal-head">
              <h2 id="ref-req-send-title">Send referral</h2>
              <button type="button" className="ref-req__modal-close" onClick={closeSendModal} aria-label="Close">
                ×
              </button>
            </div>

            {sendModal.loading ? (
              <p className="ref-req__muted">Preparing email…</p>
            ) : sendModal.error ? (
              <div className="ref-req__error">{sendModal.error}</div>
            ) : (
              <>
                <section className="ref-req__modal-section ref-req__modal-section--email">
                  <dl className="ref-req__dl">
                    <dt>To</dt>
                    <dd>
                      <a href={`mailto:${encodeURIComponent(sendModal.preview?.toEmail || sendModal.panelContact.email)}`}>
                        {sendModal.preview?.toEmail || sendModal.panelContact.email}
                      </a>
                    </dd>
                    <dt>CC</dt>
                    <dd>
                      {(sendModal.preview?.ccEmails || []).length > 0
                        ? sendModal.preview.ccEmails.join(', ')
                        : '—'}
                    </dd>
                    <dt>From</dt>
                    <dd>
                      <a href={`mailto:${encodeURIComponent(sendModal.preview?.sourceEmail || sourceEmail)}`}>
                        {sendModal.preview?.sourceEmail || sourceEmail || '—'}
                      </a>
                    </dd>
                    <dt>Subject</dt>
                    <dd>{sendModal.preview?.subject || '—'}</dd>
                    <dt>Resume</dt>
                    <dd>
                      {sendModal.preview?.resumeAttached
                        ? sendModal.preview.resumeFilename || 'Attached'
                        : '— Not on file (send will fail)'}
                    </dd>
                  </dl>
                  <h4 className="ref-req__modal-subhead">Message</h4>
                  <pre className="ref-req__email-body">{sendModal.preview?.body || ''}</pre>
                </section>

                {sendModal.sendError ? (
                  <div className="ref-req__error">{sendModal.sendError}</div>
                ) : null}

                <div className="ref-req__modal-footer">
                  <button
                    type="button"
                    className="ref-req__btn ref-req__btn--ghost"
                    onClick={closeSendModal}
                    disabled={sendModal.sending}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="ref-req__btn ref-req__btn--primary"
                    onClick={onConfirmSend}
                    disabled={
                      sendModal.sending ||
                      !sendModal.preview ||
                      !sendModal.preview?.resumeAttached ||
                      (!serverSendEnabled && !sendModal.preview?.mailto)
                    }
                  >
                    {sendModal.sending ? 'Sending…' : 'Confirm send'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
